import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Tourism Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Tourism was one of the
 * thirteen still carrying it: a two-paragraph series description under an amber note
 * saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*,
 * the same words the Utilities cut arrived in the day before — with a four-part
 * full-page capture of digitopia.co's Tourism Industry Focus page as the reference,
 * plus four files: the report mockup, Figure 1, and two overlapping crops of the
 * cover. So this is the twenty-second designed report page and the seventh focus cut
 * to arrive with **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiUtilities.ts`
 * and `content/daimiConstruction.ts` do, and `daimiTourismChecks()` feeds them into
 * the same manifest as the rest of the site's amber notes so a production build
 * cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda
 * önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That
 * band is `content/reportClosing.ts`, shared by every report page but three, and it
 * already points at the Digital & AI Maturity Report 2026 — so this page inherits
 * the answer rather than restating it, and the reference capture agrees: its Global
 * Perspective card names the 2026 report too.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These
 * documents are prerendered with no client bundle at all (see `src/prerender.tsx`),
 * so an embedded form script would never run, and the other reports on this site
 * hand the reader the PDF from our own bucket instead. So every Download button
 * here is that PDF, and `DAIMI_TO_DOWNLOAD.check` is what keeps the question of
 * gating open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working.
 */
export const DAIMI_TO_SLUG = 'digital-and-ai-maturity-report-2026-tourism-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 */
export const DAIMI_TO_INTRO = {
  body: [
    'Tourism is a high-velocity industry where dynamic demand and perishable capacity drive a strong need for digitalization. However, fragmented systems and inconsistent data currently hinder scaling and industrialization.',
    'To lead in 2026, organizations must move beyond “AI FOMO” to embed AI directly into revenue and service workflows. Success requires modernizing the core and treating experience as an end-to-end system that connects marketing, sales, and operations across the entire ecosystem.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply
   * gets a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction and Utilities cuts made.
   *
   * **With the same caveat the Utilities cover had.** It arrived as two captures,
   * "(1 of 2)" and "(2 of 2)", and they are not two halves — they are overlapping
   * crops of the same square artwork, one cut off at the bottom through the logo and
   * one cut off at the top through the title's margin. The second is the one held
   * here, because it is the only one that contains the whole design; it is
   * 2000 × 1848 rather than square, so it is missing a sliver of the top margin, and
   * the index card's square crop trims about 4% off each side. `check` asks for the
   * file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-tourism-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Tourism Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production. The live hero has no cover beside it — the right of the hero is empty but for the ribbon — and the cover is in the hero here because that is what the other designed report pages do; say if the live emptiness was the point. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the sharper one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by four lines that each open with a
 * bolded label — "Core Strengths:", "The Scaling Gap:", "Data and Integration
 * Gaps:", "Commercial Responsiveness:". Split into `label` and `body` here so the
 * label can be set rather than emphasised inside a sentence, which is what a
 * `<strong>` in the middle of a paragraph gives.
 */
export const DAIMI_TO_FINDINGS = {
  heading: 'DAIMI Findings: Experience vs. Fragmentation',
  lede: 'The 2026 benchmark indicates a wide spread in vitality performance, with most tourism organizations currently clustered at mid-level maturity.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in customer experience, where the best players design seamless journeys across search, booking, and post-trip engagement.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Progress is frequently hindered by fragmentation, as organizations operate with multiple disconnected systems across property operations, loyalty, and partner distribution.',
    },
    {
      label: 'Data and Integration Gaps',
      body: 'Customer identity is often fragmented across channels and intermediaries, which limits personalization and makes AI outputs harder to operationalize.',
    },
    {
      label: 'Commercial Responsiveness',
      body: 'Leaders differentiate through the ability to adjust pricing and manage inventory rapidly in response to demand signals.',
    },
  ],
  /**
   * Figure 1 itself — the vitality scatter supplied with this request, the same
   * shape the Airlines, Airports and Construction cuts carry rather than the
   * portrait score range the Utilities cut does. It is 1200 × 1185 on a white
   * ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-tourism-vitality-chart.webp',
    alt: 'DAIMI vitality chart for the tourism industry: 76 companies plotted on digital velocity against digital endurance, most clustered between 2.0 and 3.0 on both axes',
  } satisfies Photo,
  caption: 'Figure 1: DAIMI Vitality Chart – Tourism Industry',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole; its own furniture says “Company Count 76”, which is the company count this page therefore states in the alt text and nowhere else — the live caption carries neither it nor a year. What wants a reading against digitopia.co is the prose: the lede and the four findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_TO_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Tourism Industry',
  body: [
    'To lead in 2026, tourism executives must modernize the core platforms that currently act as a bottleneck for real-time decisions and automation. Success requires shifting from local, site-specific wins to a portfolio-scale roadmap that industrializes what works across all brands and geographies. By strengthening ecosystem interoperability and upskilling frontline teams, firms can turn high volumes of customer interactions into a compounding AI-driven advantage.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn tourism complexity into scalable, high-performance execution.',
  ],
  /** The mockup supplied with this request — the tourism cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-tourism-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Tourism Focus',
  } satisfies Photo,
  check:
    'Two things here. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And on the live page this band is text only: the right half of that row is empty, and the report mockup you attached does not appear on the page anywhere. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every
 * report page but three — and the reference capture for this page shows exactly
 * that pair, pointing at the Digital & AI Maturity Report 2026, which is the "2024
 * değil 2026" the request asked for. Nothing to change: the shared band already
 * links to `DAIMI_SLUG`.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_TO_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Tourism Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiUtilitiesChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures and its cover. Three of the notes are a
 * transcription nobody has read against the original, and one is a decision about
 * gating that belongs to marketing.
 */
export function daimiTourismChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_TO_INTRO.check },
    { section: 'findings', check: DAIMI_TO_FINDINGS.check },
    { section: 'analysis', check: DAIMI_TO_ANALYSIS.check },
    { section: 'download', check: DAIMI_TO_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_TO_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
